import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Can, usePermission } from '../lib/permissions';
import { LayoutDashboard, Grid3x3, Users, BarChart3, Settings, Target, Activity, Award, ShieldCheck, Building2, Briefcase, ChevronDown, Shield, MessageSquare, Calendar, Sun, Moon, ChevronLeft, LogOut, HelpCircle, Check, PhoneIncoming, PhoneOff, PhoneCall } from 'lucide-react';
import api from '../lib/api';
import NotificationBell from '../components/NotificationBell';
import UserMenu from '../components/UserMenu';
import GlobalSearch from '../components/GlobalSearch';
import { useStore } from '../store/useStore';
import { BACKEND_URL } from '../lib/api';
import { usePresence } from '../lib/presence';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import BexcodeLogo from '../components/BexcodeLogo';

const getAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

export function Avatar({ user, size = 32, showStatus = true }) {
  const presence = usePresence();
  const statuses = presence?.statuses || {};
  const initials = (user?.full_name || user?.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  const statusInfo = user?.id ? statuses[user.id] : null;
  const status = statusInfo?.presence_status || user?.presence_status || 'offline';

  const statusColors = {
    online: '#48BB78',
    busy: '#E53E3E',
    away: '#ECC94B',
    offline: '#A0AEC0'
  };

  const dotColor = statusColors[status] || '#A0AEC0';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div className="tm-avatar" style={{
        width: size, height: size, fontSize: size / 2.5,
        background: user?.avatar_url ? `url(${getAvatarUrl(user.avatar_url)}) center/cover` : (user?.avatar_color || '#0079BF'),
      }} data-testid="user-avatar">
        {!user?.avatar_url && initials}
      </div>
      {showStatus && user?.id && (
        <span
          style={{
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: size * 0.32,
            height: size * 0.32,
            minWidth: 7,
            minHeight: 7,
            borderRadius: '50%',
            backgroundColor: dotColor,
            border: '2px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.2)'
          }}
          title={statusInfo?.custom_status_msg ? `${status} - "${statusInfo.custom_status_msg}"` : status}
          data-testid={`presence-dot-${status}`}
        />
      )}
    </div>
  );
}


// ============ Admin dropdown ============
const ADMIN_ITEMS = [
  { path: '/team', label: 'Users', Icon: Users, perm: 'users.view' },
  { path: '/roles', label: 'Roles', Icon: ShieldCheck, perm: 'roles.view' },
  { path: '/settings', label: 'Settings', Icon: Settings, perm: 'settings.view' },
];

function AdminMenu() {
  const { has } = usePermission();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const items = ADMIN_ITEMS.filter((i) => has(i.perm));
  useEffect(() => {
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  if (!items.length) return null;
  const active = items.some((i) => location.pathname.startsWith(i.path));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((v) => !v)} data-testid="nav-admin"
        className={active ? 'active' : ''}
        style={{ background: 'transparent', border: 'none', color: 'inherit', font: 'inherit', cursor: 'pointer', padding: '6px 10px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Shield size={14} /> Admin <ChevronDown size={12} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
      </button>
      {open && (
        <div className="tm-pop" data-testid="admin-dropdown"
          style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, minWidth: 200, background: 'var(--trello-card-bg)', borderRadius: 6, boxShadow: '0 8px 16px rgba(0,0,0,0.18)', border: '1px solid var(--trello-border)', padding: 6, zIndex: 100 }}>
          {items.map(({ path, label, Icon }) => (
            <NavLink key={path} to={path} data-testid={`admin-item-${label.toLowerCase()}`}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 4,
                color: isActive ? '#fff' : 'var(--trello-text)',
                background: isActive ? 'var(--trello-blue)' : 'transparent',
                textDecoration: 'none', fontSize: 13, fontWeight: 500,
              })}
              className="hover:bg-slate-100">
              <Icon size={14} />
              {label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

import { useChatSocket } from '../hooks/useChatSocket';

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const { has } = usePermission();
  const nav = useNavigate();

  const { unreadCounts, setActiveUser, incomingCall, setIncomingCall, setPendingHuddleJoin, socket } = useStore();
  const [company, setCompany] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (user) {
      api.get('/settings/company')
        .then(({ data }) => setCompany(data.company))
        .catch(err => console.error('Error loading company in layout', err));
    }
  }, [user]);

  const toggleLanguage = () => {
    const nextLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(nextLang);
    localStorage.setItem('language', nextLang);
  };
  const totalUnread = Object.values(unreadCounts).reduce((sum, val) => sum + (val || 0), 0);

  // Connect Socket.io connection globally
  useChatSocket();

  useEffect(() => {
    if (user) {
      setActiveUser(user);
    } else {
      setActiveUser(null);
    }
  }, [user, setActiveUser]);

  const companyInitials = (name) => {
    if (!name) return 'TM';
    return name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
  };

  const navLinkStyle = ({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: collapsed ? 0 : 12,
    justifyContent: collapsed ? 'center' : 'flex-start',
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
    color: isActive ? '#0079bf' : 'var(--trello-text-muted)',
    background: isActive ? 'rgba(0, 121, 191, 0.08)' : 'transparent',
    transition: 'all 0.2s ease',
  });

  const roleLabels = {
    leader: 'Org Admin',
    manager: 'Manager',
    team: 'Team Member'
  };
  const userRoleLabel = roleLabels[user.role] || user.role;

  if (!user) return null;

  const doLogout = async () => { await logout(); nav('/login'); };
  const hasCommandAccess = has('dashboard.view_command');
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--trello-bg)' }}>
      {/* Left Sidebar Navigation */}
      <nav style={{
        width: collapsed ? 72 : 240,
        height: '100vh',
        position: 'sticky',
        top: 0,
        background: 'var(--trello-card-bg)',
        borderRight: '1px solid var(--trello-border)',
        display: 'flex',
        flexDirection: 'column',
        padding: collapsed ? '24px 12px' : '24px 16px',
        boxSizing: 'border-box',
        flexShrink: 0,
        zIndex: 50,
        transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), padding 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
      }} className="hidden md:flex" data-testid="main-nav">

        {/* Sidebar Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', padding: '12px 8px', borderBottom: '1px solid var(--trello-border)', marginBottom: 12 }}>
          <BexcodeLogo size={28} showText={!collapsed} />
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer', padding: 4, flexShrink: 0, marginLeft: collapsed ? 0 : 8 }}
            data-testid="toggle-sidebar"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <ChevronLeft size={16} style={{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
        </div>


        {/* Navigation Links list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, overflowY: 'auto' }}>
          <NavLink to="/dashboard" style={navLinkStyle}>
            <LayoutDashboard size={16} />
            {!collapsed && <span>Dashboard</span>}
          </NavLink>
          {hasCommandAccess && (
            <NavLink to="/command-center" style={navLinkStyle}>
              <Target size={16} />
              {!collapsed && <span>My Tasks</span>}
            </NavLink>
          )}
          <NavLink to="/projects" style={navLinkStyle}>
            <Grid3x3 size={16} />
            {!collapsed && <span>Projects</span>}
          </NavLink>

          {has('reports.view') && (
            <NavLink to="/reports" style={navLinkStyle}>
              <BarChart3 size={16} />
              {!collapsed && <span>Reports</span>}
            </NavLink>
          )}
          {has('chat.view') && (
            <NavLink to="/chat" style={navLinkStyle} title="Chat">
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <MessageSquare size={16} />
                {totalUnread > 0 && collapsed && (
                  <span style={{ position: 'absolute', top: -6, right: -8, background: '#ef4444', color: '#fff', borderRadius: '50%', fontSize: 8, width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </div>
              {!collapsed && <span>Chat</span>}
              {totalUnread > 0 && !collapsed && (
                <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', fontSize: 9, width: 15, height: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginLeft: 'auto' }}>
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </NavLink>
          )}
          {/* Aakash */}
          {/* <NavLink to="/google-calendar" style={navLinkStyle}>
            <Calendar size={16} />
            {!collapsed && <span>Calendar</span>}
          </NavLink> */}
          {has('rewards.view_own') && (
            <NavLink to="/rewards" style={navLinkStyle}>
              <Award size={16} />
              {!collapsed && <span>Rewards</span>}
            </NavLink>
          )}
          {has('audit.view') && (
            <NavLink to="/activity" style={navLinkStyle}>
              <Activity size={16} />
              {!collapsed && <span>Activity</span>}
            </NavLink>
          )}
          {user.is_super_admin && (
            <NavLink to="/super-admin" style={navLinkStyle}>
              <Shield size={16} />
              {!collapsed && <span>Super Admin</span>}
            </NavLink>
          )}

          {/* Settings Section */}
          {(has('settings.view') || has('users.view') || has('roles.view') || has('departments.view') || has('designations.view')) && (
            <div style={{ marginTop: 24 }}>
              {!collapsed && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--trello-text-muted)', letterSpacing: '0.05em', padding: '0 8px 8px 8px', textTransform: 'uppercase' }}>Settings</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {has('settings.view') && (
                  <NavLink to="/settings" style={navLinkStyle}>
                    <Settings size={16} />
                    {!collapsed && <span>Organization</span>}
                  </NavLink>
                )}
                {has('users.view') && (
                  <NavLink to="/team" style={navLinkStyle}>
                    <Users size={16} />
                    {!collapsed && <span>Members</span>}
                  </NavLink>
                )}
                {has('roles.view') && (
                  <NavLink to="/roles" style={navLinkStyle}>
                    <ShieldCheck size={16} />
                    {!collapsed && <span>Roles</span>}
                  </NavLink>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{ borderTop: '1px solid var(--trello-border)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 12px 8px 12px', borderBottom: '1px solid var(--trello-border-light, #f1f2f4)', marginBottom: 4 }}>

              <NotificationBell />
              {/* Aakash */}
              {/* <button onClick={toggleLanguage} style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer', fontWeight: 700, fontSize: 11, padding: 4 }} title={t('switch_language')}>
                {i18n.language.toUpperCase()}
              </button> */}

              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }} title={t('toggle_theme')}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <UserMenu
                onLogout={doLogout}
                customTrigger={
                  collapsed ? (
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%', cursor: 'pointer' }} className="hover:bg-slate-100/50 rounded-lg p-2 transition-colors">
                      <Avatar user={user} size={36} showStatus={true} />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', width: '100%', boxSizing: 'border-box', cursor: 'pointer' }} className="hover:bg-slate-100/50 rounded-lg transition-colors">
                      <Avatar user={user} size={36} showStatus={true} />
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, textAlign: 'left' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--trello-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.full_name}</span>
                        <span style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 2 }}>{userRoleLabel}</span>
                      </div>
                      <ChevronDown size={12} style={{ color: 'var(--trello-text-muted)', flexShrink: 0 }} />
                    </div>
                  )
                }
              />
            </div>

            {!collapsed && (
              <button
                onClick={doLogout}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--trello-text-muted)',
                  cursor: 'pointer',
                  padding: 8,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
                className="hover:bg-red-50 hover:text-red-500 transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>

          {!collapsed && (
            <div style={{ fontSize: 10, color: 'var(--trello-text-muted)', paddingLeft: 12, marginTop: 4 }}>
              Powered by Bexcode Pvt. Ltd.
            </div>
          )}
        </div>
      </nav>

      {/* Main Workspace Frame */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Scrollable outlet content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <Outlet />
        </div>
      </div>

      {/* Incoming Call Overlay */}
      {incomingCall && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--trello-card-bg)', padding: '24px', borderRadius: '16px', width: '320px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', textAlign: 'center', animation: 'fadeIn 0.2s ease-out' }}>
            <div style={{ position: 'relative', width: '80px', height: '80px', margin: '0 auto 16px' }}>
              <div style={{ position: 'absolute', inset: -8, borderRadius: '50%', border: '2px solid #ef4444', animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
              <img src={getAvatarUrl(incomingCall.callerAvatar) || '/default-avatar.png'} alt="Caller" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--trello-card-bg)', position: 'relative', zIndex: 1 }} onError={(e) => e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(incomingCall.callerName) + '&background=random'} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 600, color: 'var(--trello-text)' }}>{incomingCall.callerName}</h3>
            <p style={{ margin: '0 0 24px', color: 'var(--trello-text-muted)', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <PhoneIncoming size={14} className="text-green-500 animate-pulse" />
              Incoming Huddle Call...
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  if (socket) socket.emit('huddle:reject', { callerId: incomingCall.callerId, channelId: incomingCall.channelId });
                  setIncomingCall(null);
                }}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
              >
                <PhoneOff size={18} /> Decline
              </button>
              <button
                onClick={() => {
                  setPendingHuddleJoin(incomingCall.channelId);
                  setIncomingCall(null);
                  nav('/chat');
                }}
                style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', background: '#22c55e', color: '#fff', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#16a34a'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#22c55e'}
              >
                <PhoneCall size={18} /> Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
